import React, { JSXElementConstructor, ReactNode } from 'react';
import { ClientEnv, AnalyticsConnectionType, SVGProps } from '@repo/utils/types';
import analytics from '@repo/utils/helpers/analytics';
import { PlusIcon, XIcon } from '@repo/icons';
import Integration, {
  DeviceIntegrationProvider,
  IntegrationType,
  IntegrationProvider,
  titleForDeviceIntegrationType,
} from '@repo/utils/models/Integration';
import { capitalize } from '@repo/utils/helpers/string';

export interface IntegrationOptionProps {
  className?: string;
  label: ReactNode;
  provider: IntegrationProvider;
  onClick?: () => void;
  lastImport?: string;

  integrationType?: IntegrationType;
  isInOnboarding?: boolean;
  altIcon?: ReactNode;

  // still using these anywhere??
  isConnected?: boolean;
  btnIcon?: JSXElementConstructor<SVGProps>;
  isSupported?: boolean;
  whereSupported?: Array<ClientEnv>;
}

export const inlineLabelForDeviceIntegrationType: Record<DeviceIntegrationProvider, string> = {
  ...titleForDeviceIntegrationType,
  'apple-phone-log': 'phone call history',
};

export const supportLinkSlugForDeviceIntegrationType: Record<DeviceIntegrationProvider, string> = {
  'apple-messages': 'imessage',
  'apple-contacts': 'contacts',
  'facebook': 'facebook',
  'linkedin': 'linkedin',
  'contacts': 'contacts',
  'apple-calendar': 'calendar',
  'apple-phone-log': 'phone',
  'twitter-platforms': 'twitter',
  'whatsapp': 'whatsapp',
  'instagram': 'instagram',
};

export const RemoveMessage = () => (
  <>
    <p>Do you want to keep a copy of your contacts and moments?</p>
    <p>
      If you don't keep a copy, all contacts and moments created via this account will be deleted.
    </p>
  </>
);

export const AddIntegrationIcon = ({ className }: SVGProps) => (
  <PlusIcon className={className} width={14} height={14} strokeWidth={2.2} />
);

export const RemoveIntegrationIcon = ({ className, ...props }: SVGProps) => (
  <XIcon className={className} width={12} height={12} {...props} />
);

type ScopeType = 'email' | 'calendar' | 'all';

export const trackConnectionScreen = (
  props: {
    type?: AnalyticsConnectionType;
    scopes?: ScopeType;
  } = {},
) => {
  analytics.track('Onboarding in Progress', { screen: 'Connect Accounts', ...props });
};

// We are unable to support the Apple device integrations in MAS at this time because of
// sandbox file access restrictions. Until this can be addressed, users should be able
// to delete these integrations (if connected) from MAS, but not add or sync them.
export const unsupportedInMAS = (provider: IntegrationProvider) =>
  ['apple-messages', 'apple-phone-log'].includes(provider);

export interface TrackingProps {
  type?: AnalyticsConnectionType;
  scopes?: ScopeType;
}

export type TrackingFn = (props: TrackingProps) => void;

interface DisconnectOpts {
  integration: Integration;
  deleteIntegration: () => void;
  archiveIntegration: () => void;
}

export const promptToDisconnectConsolidatedIntegration = ({
  integration,
  deleteIntegration,
}: Omit<DisconnectOpts, 'archiveIntegration'>) => {
  window.app.confirm(
    {
      title: `Remove ${capitalize(integration.provider)} Connection`,
      message: `Are you sure you want to remove your ${integration.type} connection?`,
      action: 'Remove',
      variant: 'danger',
      forceDarkMode: true,
    },
    (confirmed) => {
      if (confirmed) {
        deleteIntegration();
      }
    },
  );
};

export const promptToDisconnectIntegration = ({
  integration,
  deleteIntegration,
  archiveIntegration,
}: DisconnectOpts) => {
  window.app.confirm(
    {
      title: integration.displayName,
      message: <RemoveMessage />,
      actions: [
        { title: 'Delete', variant: 'danger', value: 'delete' },
        { title: 'Keep Copy', variant: 'primary', value: 'archive', autoFocus: true },
      ],
    },
    (action) => {
      if (action == 'delete') {
        deleteIntegration();
      } else if (action == 'archive') {
        archiveIntegration();
      }
    },
  );
};

export const promptToResetNotionIntegration = (syncIntegration: () => void) => {
  window.app.confirm(
    {
      title: 'Erase & Reimport Notion',
      message: (
        <>
          Erasing and reimporting Clay to Notion requires us to clear the contents of your existing
          Notion <b>Clay Contacts</b> database before proceeding. Because Clay has a limited ability
          to change your Notion workspace, the entire resync can take up to several minutes to
          complete depending on the size of your network.
        </>
      ),
      action: 'Erase & Reimport',
      variant: 'primary',
    },
    (confirmed) => {
      if (confirmed) {
        syncIntegration();
      }
    },
  );
};

export const promptToDisconnectNotionIntegration = ({
  deleteIntegration,
}: Omit<DisconnectOpts, 'integration' | 'archiveIntegration'>) => {
  window.app.confirm(
    {
      title: 'Notion',
      message: (
        <>
          When you disconnect this integration, Clay will stop syncing your contact data to Notion.
          Your <b>Clay Contacts</b> database that has been created in Notion will not be deleted.
        </>
      ),
      action: 'Disconnect',
      variant: 'danger',
    },
    (confirmed) => {
      if (confirmed) {
        deleteIntegration();
      }
    },
  );
};
